/**
 * React Query hooks for Projects API
 * Provides data fetching, caching, and mutations for project-related operations
 *
 * Cache Strategy (Industry Standard - Facebook/Relay pattern):
 * - Uses centralized projectCache for entity updates
 * - Update once -> reflects everywhere automatically
 * - No manual cache synchronization across queries
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useEffect } from 'react';
import { createAuthHeaders, getApiBaseUrl } from '@/utils/apiUtils';
import { fetchWithAuthRetry } from '@/utils/authErrorHandler';
import { socketService } from '@/services/socketService';
import { queryKeys } from '@/utils/queryKeys';
import { devError } from '@/utils/devLog';

// Types
interface CreatorInfo {
  name: string;
  email: string;
}

interface Project {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'approved' | 'rejected';
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  // Extended fields that may be present
  title?: string;
  summary?: string;
  project_type?: string;
  custom_project_type?: string;
  locations?: string[];
  sector?: string[];
  custom_sectors?: string[];
  investment_size?: string;
  expected_return?: string;
  required_capital?: number;
  estimated_roi?: string;
  thumbnail_url?: string;
  creatorInfo?: CreatorInfo;
  approval_status?: 'pending' | 'approved' | 'rejected';
  is_visible?: boolean;
  organizationId?: string;
  submitted_for_review?: boolean;
  submittedAt?: string;
  rejection_reason?: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface ProjectsResponse {
  projects: Project[];
  totalCount: number;
  pagination?: PaginationInfo;
}

interface CapitalStackData {
  // Define capital stack structure based on your API
  [key: string]: unknown;
}

interface ProjectDetailsData {
  projectData: Project | null;
  capitalStack: CapitalStackData | null;
  projectId: string;
}

export type ProjectFilters = {
  status?: string;
  category?: string;
  search?: string;
  sector?: string | string[];
  location?: string | string[];
  investmentMin?: number;
  investmentMax?: number;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  view?: 'public';
};

/**
 * Standalone fetch function for projects — used by both useProjects and prefetchProjectsForArtie.
 */
export async function fetchProjects(
  filters: ProjectFilters | undefined,
  version: 'v1' | 'v2' = 'v2',
): Promise<ProjectsResponse> {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v));
        } else {
          params.append(key, String(value));
        }
      }
    });
  }

  const endpoint = version === 'v2' ? '/api/projects/v2' : '/api/projects';
  const url = params.toString()
    ? `${getApiBaseUrl()}${endpoint}?${params.toString()}`
    : `${getApiBaseUrl()}${endpoint}`;

  const headers = await createAuthHeaders();
  const response = await fetchWithAuthRetry(url, { headers }, 'Projects: Fetch Projects');

  if (!response.ok) {
    throw new Error(`Failed to fetch projects: ${response.statusText}`);
  }
  const data = await response.json();

  if (version === 'v2') {
    return {
      projects: data.projects || [],
      totalCount: data.pagination?.total || data.totalCount || (data.projects ? data.projects.length : 0),
      pagination: data.pagination,
    } as ProjectsResponse;
  } else {
    const projects = Array.isArray(data) ? data : (data.projects || []);
    return {
      projects,
      totalCount: projects.length,
      pagination: undefined,
    } as ProjectsResponse;
  }
}

/**
 * Hook to fetch all projects with optional filters
 * Supports both v1 and v2 API endpoints with enhanced filtering
 */
export function useProjects(
  filters?: ProjectFilters,
  version: 'v1' | 'v2' = 'v2',
  options?: { enabled?: boolean; staleTime?: number; gcTime?: number },
) {
  return useQuery({
    queryKey: ['projects', version, filters],
    queryFn: () => fetchProjects(filters, version),
    staleTime: options?.staleTime ?? 1000 * 60 * 2, // default 2 minutes
    gcTime: options?.gcTime ?? 1000 * 60 * 5, // default 5 minutes
    // Keep old data visible while new query loads (prevents flickering on admin toggle, filter changes)
    placeholderData: keepPreviousData,
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });
}

/**
 * Hook to fetch detailed project data including all sections
 */
export function useProjectDetails(projectId: string | undefined) {

  // FIX: Move subscription out of queryFn to prevent duplicates on refetch/retry
  useEffect(() => {
    if (projectId) {
      socketService.subscribeToProject(projectId);
      return () => {
        // Cleanup subscription on unmount
        socketService.unsubscribeFromProject(projectId);
      };
    }
  }, [projectId]);

  return useQuery({
    queryKey: ['project-details', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required');

      const headers = await createAuthHeaders();

      // Fetch both project data and capital stack in parallel
      const [projectData, capitalStack] = await Promise.all([
        fetchWithAuthRetry(
          `${getApiBaseUrl()}/api/projects-data/${projectId}`,
          { headers },
          'Projects: Fetch Project Data'
        ).then(async res => {
          if (!res.ok) {
            const body = await res.text().catch(() => '');
            throw new Error(body || `Failed to fetch project data: ${res.status} ${res.statusText}`);
          }
          return res.json();
        }),
        fetchWithAuthRetry(
          `${getApiBaseUrl()}/api/projects/${projectId}/capital-stack`,
          { headers },
          'Projects: Fetch Capital Stack'
        ).then(async res => {
          if (!res.ok) {
            devError('Error fetching capital stack:', res.statusText);
            return null;
          }
          return res.json();
        }).catch(err => {
          devError('Error fetching capital stack:', err);
          return null;
        })
      ]);

      // REMOVED: Socket subscription (moved to useEffect above)

      // Combine the data
      return {
        projectData,
        capitalStack,
        projectId
      } as ProjectDetailsData;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch project capital stack
 */
export function useProjectCapitalStack(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-capital-stack', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required');

      const headers = await createAuthHeaders();
      const response = await fetchWithAuthRetry(
        `${getApiBaseUrl()}/api/projects/${projectId}/capital-stack`,
        { headers },
        'Projects: Fetch Capital Stack'
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch capital stack: ${response.statusText}`);
      }
      return response.json() as Promise<CapitalStackData>;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}


/**
 * Hook to fetch user's own projects
 */
export function useUserProjects(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.projects.byUser,
    queryFn: async () => {
      const headers = await createAuthHeaders();
      const response = await fetchWithAuthRetry(
        `${getApiBaseUrl()}/api/projects-data?limit=500`,
        { headers },
        'Projects: Fetch User Projects'
      );

      if (!response.ok) {
        throw new Error('Failed to fetch user projects');
      }
      const data = await response.json();
      return { projects: data.projects || [], totalCount: data.totalCount || 0 } as ProjectsResponse;
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });
}
